import { useAppDispatch, useAppSelector } from "src/store/store";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import {
  resetGroundTruthReviewPage,
  updateConfirmedAnswersFromHistory,
  extractTextHighlightByFieldDefinition,
  setListViewIndex,
  addReviewEntry,
  updateGroundTruthData,
  setShouldShowError,
  sortAndFilterGroundTruthDataListView,
} from "src/store/modelLifeCycleGroundTruthSlice";
import {
  resetGetSiteDetailsLoading,
  getSiteDetails,
  getSiteDetailsV2,
} from "src/store/modelManagementSlice";
import {
  findNextUnreviewedCase,
  hasNoLLMClarification,
  isCurrentUserAdminReview,
  isGroundTruthConfirmedAnswerValid,
} from "src/components/shared/groundTruth/reviewTable/helpers";
import { createToast } from "src/store/toastsSlice";
import { TOAST_TIMEOUT } from "src/config/Toast";
import { isAdminUser } from "src/utils/permissionUtil";

export const usePostUpdateNavigation = (
  navigateUrl: string,
  sourceId?: string,
  pageId?: string,
) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { groundTruthDataListView, updateGroundTruthDataLoading, moveToNextWhenSave } =
    useAppSelector((state) => state.modelLifeCycleGroundTruthSlice);
  const { alias } = useAppSelector((state) => state.user);

  useEffect(() => {
    if (updateGroundTruthDataLoading === "fulfilled" && sourceId) {
      dispatch(resetGroundTruthReviewPage());
      dispatch(resetGetSiteDetailsLoading());
      if (moveToNextWhenSave) {
        const next = findNextUnreviewedCase(groundTruthDataListView, alias, sourceId);
        if (next) {
          navigate(`/${navigateUrl}/${pageId}/${next.sourceId}`);
        } else {
          navigate(`/${navigateUrl}/${pageId}`);
          dispatch(
            createToast({
              type: "success",
              message: "All record have finished reviewed, redirect to list view page...",
              timeout: TOAST_TIMEOUT,
            }),
          );
        }
      }
    }
  }, [updateGroundTruthDataLoading, sourceId]);
};

export const useGroundTruthDataEffects = () => {
  const dispatch = useAppDispatch();
  const { alias } = useAppSelector((state) => state.user);
  const {
    selectedGroundTruthData,
    getGroundTruthDataLoading,
    showHighlight,
    textHighlightLoading,
    getListViewLoading,
  } = useAppSelector((state) => state.modelLifeCycleGroundTruthSlice);
  const { selectedModel, getModelByIdLoading, getSiteDetailsLoading } = useAppSelector(
    (state) => state.modelSlice,
  );

  const previousModelId = useRef<string>("");

  useEffect(() => {
    if (
      getGroundTruthDataLoading === "fulfilled" &&
      previousModelId.current !== selectedGroundTruthData.modelId
    ) {
      previousModelId.current = selectedGroundTruthData.modelId;
    }
  }, [getGroundTruthDataLoading, selectedGroundTruthData]);

  useEffect(() => {
    if (getGroundTruthDataLoading === "fulfilled") {
      dispatch(updateConfirmedAnswersFromHistory(alias));
    }
  }, [getGroundTruthDataLoading]);

  useEffect(() => {
    if (
      getGroundTruthDataLoading === "fulfilled" &&
      showHighlight &&
      textHighlightLoading === "idle" &&
      hasNoLLMClarification(selectedGroundTruthData)
    ) {
      dispatch(
        extractTextHighlightByFieldDefinition({
          text: selectedGroundTruthData.sourceText,
          modelId: selectedGroundTruthData.modelId,
        }),
      );
    }
    if (showHighlight && textHighlightLoading === "fulfilled") {
      dispatch(
        createToast({
          type: "success",
          message: "LLM extract highlight success, click field name to view highlight",
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
  }, [getGroundTruthDataLoading, showHighlight, textHighlightLoading]);

  useEffect(() => {
    if (
      getModelByIdLoading === "fulfilled" &&
      getGroundTruthDataLoading === "fulfilled" &&
      selectedGroundTruthData.metadata?.siteCode &&
      getSiteDetailsLoading === "idle" &&
      !selectedGroundTruthData.metadata?.showFullLocationList
    ) {
      // Check which API calls are needed based on field configurations
      if (
        Object.values(selectedModel.fields).some(
          (field) => field.optionConfig?.action === "site_location_mapping",
        )
      ) {
        dispatch(getSiteDetails(selectedGroundTruthData.metadata.siteCode));
      }
      if (
        Object.values(selectedModel.fields).some(
          (field) => field.optionConfig?.action === "site_location_mapping_v2",
        )
      ) {
        dispatch(getSiteDetailsV2(selectedGroundTruthData.metadata.siteCode));
      }
    }
  }, [
    selectedModel,
    getGroundTruthDataLoading,
    getModelByIdLoading,
    selectedGroundTruthData,
    getSiteDetailsLoading,
  ]);
};

export const useListViewEffects = (sourceId?: string) => {
  const dispatch = useAppDispatch();
  const { alias, ldap } = useAppSelector((state) => state.user);
  const { getListViewLoading, getGroundTruthDataLoading } = useAppSelector(
    (state) => state.modelLifeCycleGroundTruthSlice,
  );
  const { selectedModel } = useAppSelector((state) => state.modelSlice);

  useEffect(() => {
    if (getListViewLoading === "fulfilled" && selectedModel.id && sourceId) {
      dispatch(
        sortAndFilterGroundTruthDataListView({
          model: selectedModel,
          alias: alias,
          isAdmin: isAdminUser(ldap),
        }),
      );
    }
  }, [getListViewLoading, selectedModel, sourceId]);

  useEffect(() => {
    if (getListViewLoading === "fulfilled" && getGroundTruthDataLoading === "fulfilled") {
      dispatch(setListViewIndex(sourceId));
    }
  }, [getListViewLoading, getGroundTruthDataLoading]);
};

export const useNavigationHandlers = (baseUrl: string, pageId?: string) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { groundTruthDataListView, groundTruthListIndex } = useAppSelector(
    (state) => state.modelLifeCycleGroundTruthSlice,
  );

  const onLeftChevronClick = () => {
    if (groundTruthDataListView[groundTruthListIndex - 1]) {
      const url = pageId
        ? `/${baseUrl}/${pageId}/${groundTruthDataListView[groundTruthListIndex - 1].sourceId}`
        : `/${baseUrl}/${groundTruthDataListView[groundTruthListIndex - 1].sourceId}`;
      navigate(url);
    } else {
      const url = pageId ? `/${baseUrl}/${pageId}` : `/${baseUrl}`;
      navigate(url);
    }
  };

  const onRightChevronClick = () => {
    dispatch(resetGroundTruthReviewPage());
    dispatch(resetGetSiteDetailsLoading());
    if (groundTruthDataListView[groundTruthListIndex + 1]) {
      const url = pageId
        ? `/${baseUrl}/${pageId}/${groundTruthDataListView[groundTruthListIndex + 1].sourceId}`
        : `/${baseUrl}/${groundTruthDataListView[groundTruthListIndex + 1].sourceId}`;
      navigate(url);
    } else {
      const url = pageId ? `/${baseUrl}/${pageId}` : `/${baseUrl}`;
      navigate(url);
    }
  };

  return { onLeftChevronClick, onRightChevronClick };
};

export const useSaveHandler = (batchJobId?: string) => {
  const dispatch = useAppDispatch();
  const { alias } = useAppSelector((state) => state.user);
  const { selectedGroundTruthData, pageLoadTimestamp } = useAppSelector(
    (state) => state.modelLifeCycleGroundTruthSlice,
  );
  const { selectedModel } = useAppSelector((state) => state.modelSlice);

  const onSaveClick = () => {
    if (isGroundTruthConfirmedAnswerValid(selectedGroundTruthData, selectedModel)) {
      if (isCurrentUserAdminReview(selectedGroundTruthData, selectedModel, alias)) {
        dispatch(
          addReviewEntry({
            groundTruthData: selectedGroundTruthData,
            reviewer: alias,
            pageLoadTimestamp: pageLoadTimestamp,
          }),
        );
      } else {
        dispatch(
          updateGroundTruthData({
            groundTruthData: selectedGroundTruthData,
            reviewer: alias,
            pageLoadTimestamp: pageLoadTimestamp,
            ...(batchJobId && { batchJobId }),
          }),
        );
      }
      dispatch(setShouldShowError(false));
    } else {
      dispatch(setShouldShowError(true));
    }
  };

  return { onSaveClick };
};
